import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSun, FaMoon, FaBars, FaTimes, FaGithub, FaLinkedin, FaFileAlt } from 'react-icons/fa';
import { NAV_SECTIONS, PORTFOLIO } from '../constants/portfolio';
import '../styles/Navbar.scss';

const Navbar = ({ darkMode, setDarkMode }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const location = useLocation();
  const isLanding = location.pathname === '/';

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  useEffect(() => {
    if (!isLanding) return undefined;

    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      const scrollPos = window.scrollY + window.innerHeight / 3;
      for (let i = NAV_SECTIONS.length - 1; i >= 0; i--) {
        const el = document.getElementById(NAV_SECTIONS[i].id);
        if (el && el.offsetTop <= scrollPos) {
          setActiveSection(NAV_SECTIONS[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isLanding]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const scrollToSection = (id) => {
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <motion.nav
        className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`}
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="navbar__inner">
          {isLanding ? (
            <button type="button" className="navbar__logo" onClick={() => scrollToSection('hero')}>
              Sai<span className="navbar__logo-accent">.</span>
            </button>
          ) : (
            <Link to="/" className="navbar__logo">
              Sai<span className="navbar__logo-accent">.</span>
            </Link>
          )}

          <button
            type="button"
            className="navbar__menu-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            {menuOpen ? <FaTimes /> : <FaBars />}
          </button>

          <div className="navbar__desktop glass">
            {isLanding ? (
              <>
                {NAV_SECTIONS.map((section) => (
                  <button
                    key={section.id}
                    type="button"
                    className={`navbar__link ${activeSection === section.id ? 'navbar__link--active' : ''}`}
                    onClick={() => scrollToSection(section.id)}
                  >
                    {activeSection === section.id && (
                      <motion.span layoutId="nav-pill" className="navbar__pill" />
                    )}
                    <span>{section.label}</span>
                  </button>
                ))}
                <Link to="/blog" className="navbar__link">Blog</Link>
              </>
            ) : (
              <>
                <Link to="/" className="navbar__link">Home</Link>
                <Link to="/blog" className="navbar__link navbar__link--active">Blog</Link>
              </>
            )}

            <span className="navbar__divider" />

            <a href={PORTFOLIO.social.github} target="_blank" rel="noopener noreferrer" aria-label="GitHub">
              <FaGithub />
            </a>
            <a href={PORTFOLIO.social.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
              <FaLinkedin />
            </a>
            <a href={PORTFOLIO.resume} target="_blank" rel="noopener noreferrer" aria-label="Resume">
              <FaFileAlt />
            </a>

            <button
              type="button"
              className="navbar__theme"
              onClick={() => setDarkMode(!darkMode)}
              aria-label="Toggle theme"
            >
              {darkMode ? <FaSun /> : <FaMoon />}
            </button>
          </div>
        </div>
      </motion.nav>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="navbar__mobile"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {isLanding ? (
              <>
                {NAV_SECTIONS.map((section, i) => (
                  <motion.button
                    key={section.id}
                    type="button"
                    className={`navbar__mobile-link ${activeSection === section.id ? 'navbar__mobile-link--active' : ''}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    onClick={() => scrollToSection(section.id)}
                  >
                    {section.label}
                  </motion.button>
                ))}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: NAV_SECTIONS.length * 0.05 }}
                >
                  <Link to="/blog" className="navbar__mobile-link" onClick={() => setMenuOpen(false)}>Blog</Link>
                </motion.div>
              </>
            ) : (
              <>
                <Link to="/" className="navbar__mobile-link">Home</Link>
                <Link to="/blog" className="navbar__mobile-link">Blog</Link>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
